import React, { useEffect, useState } from 'react'
import { Card, CardContent } from '../../ui/card'
import { Button } from '../../ui/button'
import { Label } from '../../ui/label'
import { DatePicker } from '../../ui/date-picker'
import { Combobox } from '../../ui/combobox'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { extractErrorMessage } from '@/common/extractErrorMessage'
import { useApplicationStore } from '../../../stores/useApplicationStore'
import ReportEndpoints from '../../../endpoints/ReportEndpoints'
import { ReportViewer, useReportViewer } from '../ReportViewer'
import { rootUrl } from '../reportShared'

export const ShiftAuditLogPage: React.FC = () => {
  const { identities, refreshIdentities } = useApplicationStore()
  const { viewerProps, openReport, generating, setGenerating } = useReportViewer()

  const [startDate, setStartDate] = useState<Date | undefined>()
  const [endDate, setEndDate] = useState<Date | undefined>()
  const [identityId, setIdentityId] = useState<string>('all')

  useEffect(() => {
    if (identities.length === 0) {
      refreshIdentities().catch(() => {})
    }
  }, [])

  const identityFilterOptions = [
    { value: 'all', label: 'All users' },
    ...identities.map((i) => ({
      value: i.id.toString(),
      label: `${i.firstName} ${i.lastName}`,
      sublabel: i.email || undefined,
    })),
  ]

  const generate = async () => {
    if (!startDate || !endDate) { toast.error('Select a date range'); return }
    setGenerating(true)
    try {
      const res = await ReportEndpoints.getShiftSpotAuditLogReport({
        startDate: startDate.getTime() as any,
        endDate: endDate.getTime() as any,
        userIdentityId: identityId !== 'all' ? parseInt(identityId) : undefined,
        rootUrl,
      })
      openReport('Shift & Punch Audit Log', res)
    } catch (error) { toast.error(extractErrorMessage(error, 'Failed to generate report')) }
    finally { setGenerating(false) }
  }

  return (
    <>
      <Card className="max-w-3xl">
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date *</Label>
              <DatePicker date={startDate} onDateChange={setStartDate} placeholder="Start date" />
            </div>
            <div className="space-y-2">
              <Label>End Date *</Label>
              <DatePicker date={endDate} onDateChange={setEndDate} placeholder="End date" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>For User</Label>
            <Combobox
              options={identityFilterOptions}
              value={identityId}
              onChange={setIdentityId}
              placeholder="All users"
              searchPlaceholder="Search users..."
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Matches the person who made the change or the staff member whose spot was changed
            </p>
          </div>
          <Button onClick={generate} disabled={generating}>
            {generating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Generating...</> : 'Generate Report'}
          </Button>
        </CardContent>
      </Card>
      <ReportViewer {...viewerProps} />
    </>
  )
}
